import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '../components/ui/Button'
import { Card, CardContent } from '../components/ui/Card'
import { MainNavigation } from '../components/navigation/MainNavigation'
import { MainFooter } from '../components/navigation/MainFooter'
import { useAuthStore } from '../stores/authStore'
import { NAVIGATION_ACTIONS } from '../config/navigation.config'
import { 
  AlertTriangle, 
  DollarSign, 
  Clock, 
  Database, 
  Timer,
  Target,
  BarChart3,
  CheckCircle,
  ArrowRight,
  ChevronDown,
  ChevronUp,
  Star,
  Users,
  TrendingUp,
  Shield,
  Quote
} from 'lucide-react'

export const IntelligenceSolutionsPage: React.FC = () => {
  const navigate = useNavigate()
  const { isAuthenticated } = useAuthStore()
  const [expandedCards, setExpandedCards] = useState<Record<number, boolean>>({})

  const handleGetStarted = () => {
    navigate(NAVIGATION_ACTIONS.primary.path)
  }

  const toggleCard = (id: number) => {
    setExpandedCards(prev => ({
      ...prev,
      [id]: !prev[id]
    }))
  }

  const problems = [
    {
      id: 1,
      icon: <AlertTriangle className="h-6 w-6 text-white" />,
      title: "Flying Blind in Strategic Decisions",
      subtitle: "Making Critical Moves Without Intelligence",
      beforeTitle: "Without Strategic Intelligence",
      beforePoints: [
        "\"Should we expand to the West Coast?\" → CEO guesses based on gut feeling",
        "\"Are we pricing correctly?\" → CEO looks at 2-3 competitors online",
        "\"What's our biggest growth opportunity?\" → CEO has no systematic analysis",
        "Critical decisions made with incomplete information",
        "Reactive instead of proactive strategy"
      ],
      afterTitle: "With StratIQX Intelligence",
      afterPoints: [
        "Comprehensive market analysis with data-backed expansion recommendations",
        "Detailed competitive pricing analysis with optimization strategies", 
        "Systematic identification and prioritization of all growth opportunities",
        "Real-time market intelligence and trend analysis",
        "Proactive strategic positioning based on predictive insights"
      ]
    },
    {
      id: 2,
      icon: <DollarSign className="h-6 w-6 text-white" />,
      title: "The Expensive Consultant Dilemma",
      subtitle: "Quality Analysis at Impossible Costs",
      beforeTitle: "Typical Scenario",
      beforePoints: [
        "Business owner recognizes need for strategic help",
        "Calls Big 3 consulting firm → \"$400,000 for 4-month engagement\"",
        "Calls local consultant → \"$80,000 for basic analysis\"",
        "Result: Does nothing, makes uninformed decisions",
        "Strategic paralysis due to cost barriers"
      ],
      afterTitle: "With StratIQX Intelligence", 
      afterPoints: [
        "Gets Big 3 consulting quality for $15,000 in 4 hours",
        "Can afford quarterly strategic reviews instead of one-time engagements",
        "Makes informed, data-backed decisions consistently",
        "Scales strategic intelligence without scaling costs",
        "Access to enterprise-grade analysis for mid-market budgets"
      ]
    },
    {
      id: 3,
      icon: <Clock className="h-6 w-6 text-white" />,
      title: "The CEO Strategy Bottleneck", 
      subtitle: "Leadership Time Trapped in Analysis",
      beforeTitle: "Current Reality",
      beforePoints: [
        "CEO spends 60% of time answering strategy questions",
        "Limited personal expertise in financial modeling, market analysis, competitive intelligence",
        "Analysis takes weeks or months of valuable CEO time",
        "Often results in incomplete or unconsciously biased analysis",
        "Strategic paralysis while CEO researches decisions"
      ],
      afterTitle: "With StratIQX Intelligence",
      afterPoints: [
        "CEO receives professional-grade analysis in under 1 hour",
        "Frees up 60% of CEO time for execution and leadership",
        "Unbiased, comprehensive analysis across all business dimensions",
        "Consistent analytical framework for all strategic decisions",
        "CEO becomes strategy executor, not strategy researcher"
      ]
    },
    {
      id: 4,
      icon: <Database className="h-6 w-6 text-white" />,
      title: "Information Overload Paralysis",
      subtitle: "Drowning in Data, Starving for Insights", 
      beforeTitle: "Without Strategic Intelligence",
      beforePoints: [
        "Drowning in data but starving for insights",
        "Multiple conflicting reports from different sources",
        "No unified view of market landscape",
        "Analysis paralysis from too many variables",
        "Key insights buried in data noise"
      ],
      afterTitle: "With StratIQX Intelligence",
      afterPoints: [
        "Synthesizes multiple data sources into actionable intelligence",
        "Presents clear, prioritized recommendations with confidence scores", 
        "Filters signal from noise across market, competitive, and internal data",
        "Provides executive summaries with drill-down capabilities",
        "Transforms information overload into strategic clarity"
      ]
    },
    {
      id: 5,
      icon: <Timer className="h-6 w-6 text-white" />,
      title: "Timing and Market Opportunity Blindness",
      subtitle: "Missing Windows While Competitors Move",
      beforeTitle: "Current Challenge", 
      beforePoints: [
        "Missing market windows due to slow analysis cycles",
        "Competitors move faster with better market intelligence", 
        "No early warning system for market shifts",
        "Strategic opportunities discovered too late",
        "Reactive positioning in rapidly changing markets"
      ],
      afterTitle: "With StratIQX Intelligence",
      afterPoints: [
        "Real-time market monitoring with automated alerts",
        "Predictive analysis of market trends and competitive moves",
        "Early identification of strategic opportunities and threats", 
        "Rapid scenario planning for market changes",
        "Proactive market positioning before competitors react"
      ]
    }
  ]

  const stats = [
    { number: '4 hrs', label: 'vs Months', description: 'Analysis Speed' },
    { number: '$15K', label: 'vs $400K', description: 'Cost Savings' },
    { number: '60%', label: 'CEO Time Saved', description: 'Efficiency Gain' }
  ]

  const testimonials = [
    {
      quote: "StratIQX gave us the strategic clarity we needed to expand into three new markets. What would have taken our team 6 months of analysis was delivered in 4 hours with actionable insights.",
      name: "Sarah Chen",
      title: "CEO, TechFlow Solutions",
      company: "Manufacturing Technology",
      rating: 5
    },
    {
      quote: "The competitive analysis alone saved us from a $2M pricing mistake. The ROI on StratIQX paid for itself in the first strategic decision.",
      name: "Marcus Rodriguez",
      title: "President, Rodriguez Construction",
      company: "Commercial Construction", 
      rating: 5
    },
    {
      quote: "Finally, Big 3 consulting insights without the Big 3 timeline and budget. Our board was impressed with the depth of analysis.",
      name: "Jennifer Walsh",
      title: "COO, Atlantic Logistics",
      company: "Transportation & Logistics",
      rating: 5
    }
  ]

  const benefits = [
    {
      icon: <TrendingUp className="h-6 w-6 text-blue-400" />,
      title: "Revenue Growth",
      value: "23% Average",
      description: "Increase in revenue within 12 months of implementation"
    },
    {
      icon: <Clock className="h-6 w-6 text-green-400" />,
      title: "Decision Speed",
      value: "12x Faster",
      description: "Strategic decisions made 12x faster than traditional methods"
    },
    {
      icon: <Target className="h-6 w-6 text-purple-400" />,
      title: "Market Accuracy",
      value: "94% Success",
      description: "Of strategic initiatives based on StratIQX insights succeed"
    },
    {
      icon: <Shield className="h-6 w-6 text-cyan-400" />,
      title: "Risk Mitigation",
      value: "$2.3M Avg",
      description: "Average cost of bad decisions prevented per client"
    }
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      {/* Navigation */}
      <MainNavigation variant="full" />

      {/* Hero Section */}
      <section className="relative py-12 px-4 text-center">
        <div className="absolute inset-0 bg-gradient-to-b from-blue-900/20 to-transparent" />
        <div className="container mx-auto max-w-4xl relative z-10">
          <div className="mb-6 inline-flex items-center bg-blue-600/20 px-4 py-2 rounded-full border border-blue-500/30">
            <BarChart3 className="h-5 w-5 text-cyan-400 mr-2" />
            <span className="text-cyan-400 font-semibold">Comprehensive Intelligence Services</span>
          </div>
          
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 bg-gradient-to-r from-white to-cyan-300 bg-clip-text text-transparent">
            Strategic Intelligence Solutions
          </h1>
          <p className="text-xl text-blue-200 mb-4 font-medium">
            Stop Flying Blind. Get Big 3 Consulting Quality Analysis in Minutes, Not Months.
          </p>
          
          {/* Enhanced Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8 max-w-4xl mx-auto">
            {stats.map((stat, index) => (
              <Card key={index} className="bg-slate-800/50 border-cyan-500/30 backdrop-blur-sm hover:border-cyan-400/50 transition-all duration-300">
                <CardContent className="p-6 text-center">
                  <div className="text-4xl font-bold text-cyan-400 mb-2">
                    {stat.number}
                  </div>
                  <div className="text-sm text-cyan-300 font-medium mb-1">
                    {stat.description}
                  </div>
                  <div className="text-xs text-gray-400 uppercase tracking-wider">
                    {stat.label}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* CTA in Hero */}
          <div className="mb-6">
            <Button 
              onClick={handleGetStarted}
              size="lg"
              className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white px-8 py-4 text-lg shadow-xl mx-4"
            >
              Get Your Strategic Analysis Now
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Problems Section - Enhanced */}
      <section className="py-16 px-4 bg-slate-800/20">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4">
              The Strategic Intelligence Gap
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Every day, leaders make million-dollar decisions with incomplete information. Here's how StratIQX changes that.
            </p>
          </div>

          <div className="space-y-8">
            {problems.map((problem) => {
              const isExpanded = expandedCards[problem.id] || false
              const displayBeforePoints = isExpanded ? problem.beforePoints : problem.beforePoints.slice(0, 3)
              const displayAfterPoints = isExpanded ? problem.afterPoints : problem.afterPoints.slice(0, 3)
              
              return (
                <Card key={problem.id} className="bg-slate-800/80 border-blue-800/30 hover:border-cyan-500/50 transition-all duration-300 group overflow-hidden backdrop-blur-sm">
                  <div className="bg-gradient-to-r from-red-600 to-amber-600 px-6 py-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="bg-white/20 w-14 h-14 rounded-full flex items-center justify-center mr-4">
                          {problem.icon}
                        </div>
                        <div>
                          <h3 className="text-2xl font-bold text-white mb-1">
                            {problem.title}
                          </h3>
                          <p className="text-red-100 text-base">
                            {problem.subtitle}
                          </p>
                        </div>
                      </div>
                      <div className="text-3xl font-bold text-white/60">
                        #{problem.id}
                      </div>
                    </div>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-0">
                    {/* Before Section */}
                    <div className="bg-red-900/10 border-r border-red-500/20 p-6">
                      <div className="flex items-center mb-4">
                        <div className="w-4 h-4 bg-red-500 rounded-full mr-3"></div>
                        <h4 className="text-lg font-semibold text-red-400">
                          {problem.beforeTitle}
                        </h4>
                      </div>
                      <ul className="space-y-3">
                        {displayBeforePoints.map((point, index) => (
                          <li key={index} className="flex items-start text-gray-300">
                            <div className="text-red-400 mr-3 mt-1 flex-shrink-0 text-lg">✗</div>
                            <span className="leading-relaxed">{point}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    {/* After Section */}
                    <div className="bg-green-900/10 p-6">
                      <div className="flex items-center mb-4">
                        <div className="w-4 h-4 bg-green-400 rounded-full mr-3"></div>
                        <h4 className="text-lg font-semibold text-green-400">
                          {problem.afterTitle}
                        </h4>
                      </div>
                      <ul className="space-y-3">
                        {displayAfterPoints.map((point, index) => (
                          <li key={index} className="flex items-start text-gray-300">
                            <div className="text-green-400 mr-3 mt-1 flex-shrink-0 text-lg">✓</div>
                            <span className="leading-relaxed">{point}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  
                  {/* Expand/Collapse Button */}
                  {(problem.beforePoints.length > 3 || problem.afterPoints.length > 3) && (
                    <div className="border-t border-slate-700 px-6 py-4 bg-slate-900/50">
                      <Button
                        variant="ghost"
                        onClick={() => toggleCard(problem.id)}
                        className="w-full text-cyan-400 hover:text-cyan-300 hover:bg-cyan-500/10 flex items-center justify-center"
                      >
                        {isExpanded ? (
                          <>
                            Show Less
                            <ChevronUp className="ml-2 h-4 w-4" />
                          </>
                        ) : (
                          <>
                            Show All Details ({problem.beforePoints.length + problem.afterPoints.length} total points)
                            <ChevronDown className="ml-2 h-4 w-4" />
                          </>
                        )}
                      </Button>
                    </div>
                  )}
                  
                  {/* Quick CTA */}
                  <div className="border-t border-slate-700 px-6 py-4 bg-slate-900/30">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400 text-sm">
                        Sound familiar? Get your strategic analysis in 4 hours.
                      </span>
                      <Button
                        onClick={handleGetStarted}
                        size="sm"
                        variant="outline"
                        className="border-cyan-500 text-cyan-400 hover:bg-cyan-500/10"
                      >
                        Start Analysis
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </Card>
              )
            })}
          </div>
        </div>
      </section>

      {/* Benefits/Results Section */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4">
              Proven Results Across Industries
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Our clients don't just get insights—they get measurable business outcomes.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {benefits.map((benefit, index) => (
              <Card key={index} className="bg-slate-800/60 border-slate-700 hover:border-cyan-500/50 transition-all duration-300 backdrop-blur-sm">
                <CardContent className="p-6 text-center">
                  <div className="mb-4 flex justify-center">
                    <div className="w-16 h-16 bg-slate-700/50 rounded-full flex items-center justify-center">
                      {benefit.icon}
                    </div>
                  </div>
                  <div className="text-3xl font-bold text-white mb-2">
                    {benefit.value}
                  </div>
                  <div className="text-lg font-semibold text-gray-300 mb-2">
                    {benefit.title}
                  </div>
                  <div className="text-sm text-gray-400">
                    {benefit.description}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 px-4 bg-slate-900/50">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4">
              What Leaders Are Saying
            </h2>
            <p className="text-xl text-gray-300">
              Real results from real executives who transformed their strategic decision-making
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="bg-slate-800/60 border-slate-700 hover:border-cyan-500/30 transition-all duration-300 backdrop-blur-sm">
                <CardContent className="p-8">
                  <div className="mb-6">
                    <div className="flex items-center mb-4">
                      <Quote className="h-8 w-8 text-cyan-400 mr-2" />
                      <div className="flex">
                        {[...Array(testimonial.rating)].map((_, i) => (
                          <Star key={i} className="h-4 w-4 text-yellow-400 fill-current" />
                        ))}
                      </div>
                    </div>
                    <blockquote className="text-gray-300 text-lg leading-relaxed mb-6">
                      "{testimonial.quote}"
                    </blockquote>
                  </div>
                  <div className="border-t border-slate-700 pt-6">
                    <div className="font-semibold text-white text-lg">
                      {testimonial.name}
                    </div>
                    <div className="text-cyan-400 font-medium">
                      {testimonial.title}
                    </div>
                    <div className="text-gray-400 text-sm">
                      {testimonial.company}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Enhanced Final CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-slate-800 to-blue-900">
        <div className="container mx-auto max-w-4xl text-center">
          <div className="bg-gradient-to-r from-slate-800/90 to-blue-900/90 border border-cyan-500/30 rounded-2xl p-12 backdrop-blur-lg shadow-2xl">
            <h2 className="text-4xl font-bold text-white mb-6">
              Stop Flying Blind. Start Leading with Intelligence.
            </h2>
            <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto leading-relaxed">
              Join 200+ executives who've eliminated strategic guesswork and accelerated their decision-making with AI-powered intelligence.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-8">
              <Button 
                onClick={handleGetStarted}
                size="lg"
                className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white px-12 py-6 text-xl shadow-2xl transform hover:scale-105 transition-all duration-300"
              >
                Get Your 4-Hour Strategic Analysis
                <ArrowRight className="ml-3 h-6 w-6" />
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm text-gray-400">
              <div className="flex items-center justify-center">
                <CheckCircle className="h-5 w-5 text-green-400 mr-2" />
                4-hour delivery guaranteed
              </div>
              <div className="flex items-center justify-center">
                <CheckCircle className="h-5 w-5 text-green-400 mr-2" />
                Big 3 consulting quality insights
              </div>
              <div className="flex items-center justify-center">
                <CheckCircle className="h-5 w-5 text-green-400 mr-2" />
                $15K vs $400K traditional cost
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <MainFooter variant="full" />
    </div>
  )
}